import { Map } from "@iventis/domain-model/model/map";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";

/** Given a sitemap version is selected, the map's sitemap configs are updated */
export function onVersionSelected(mapId: string, sitemaps: Sitemap[], sitemapConfigs: MapSitemapConfig[], sitemapId: string, versionId: string) {
    const updatedMap: Partial<Map> = {
        id: mapId,
    };
    const sitemap = sitemaps.find((s) => s.id === sitemapId);
    if (sitemap == null) {
        throw new Error("Tried to select a version for a sitemap which does not exist.");
    }
    if (sitemap.versions === undefined) {
        throw new Error("Sitemap has no versions.");
    }
    const version = sitemap.versions.find((v) => v.id === versionId);
    if (version == null) {
        throw new Error("Version doesn't exist.");
    }
    if (sitemapConfigs.length === 0 || !sitemapConfigs.some((sc) => sc.sitemapId === sitemapId)) {
        if (version.type === SitemapVersionType.TwoDimensional) {
            const updatedConfigs = [
                ...sitemapConfigs,
                {
                    sitemapId,
                    version2dId: versionId,
                    version2dVisible: true,
                },
            ];
            updatedMap.sitemapConfigs = updatedConfigs;
        } else if (version.type === SitemapVersionType.ThreeDimensional) {
            const updatedConfigs = [
                ...sitemapConfigs,
                {
                    sitemapId,
                    version3dId: versionId,
                    version3dVisible: true,
                },
            ];
            updatedMap.sitemapConfigs = updatedConfigs;
        }
    } else {
        const index = sitemapConfigs.findIndex((sc) => sc.sitemapId === sitemapId);
        const updatedConfigs = [...sitemapConfigs];
        if (version.type === SitemapVersionType.TwoDimensional) {
            updatedConfigs[index] = {
                ...updatedConfigs[index],
                version2dId: versionId,
                version2dVisible: true,
            };
        } else if (version.type === SitemapVersionType.ThreeDimensional) {
            updatedConfigs[index] = {
                ...updatedConfigs[index],
                version3dId: versionId,
                version3dVisible: true,
            };
        }
        updatedMap.sitemapConfigs = updatedConfigs;
    }
    return updatedMap;
}

/** When passed in a sitemapId, sitemaps and sitemap configs, returns either a version from the sitemap configs, the latest sitemap version or a blank string */
export function getVersionId(sitemapId: string, sitemapConfigs: MapSitemapConfig[], sitemaps: Sitemap[], versionType: SitemapVersionType): string {
    const config = sitemapConfigs.find((sc) => sc.sitemapId === sitemapId);
    if (config !== undefined) {
        if (versionType === SitemapVersionType.TwoDimensional && config.version2dVisible === true) {
            return config.version2dId;
        }
        if (versionType === SitemapVersionType.ThreeDimensional && config.version3dVisible === true) {
            return config.version3dId;
        }
        return "";
    }
    return getLatestVersionId(sitemapId, sitemaps, versionType);
}

/** Given a sitemap is selected or deselected, the map's sitemap configs are updated */
export function onSitemapSelected(
    mapId: string,
    sitemaps: Sitemap[],
    sitemapConfigs: MapSitemapConfig[],
    sitemapId: string,
    visible: boolean,
    versionType: SitemapVersionType = null
) {
    const updatedMap: Partial<Map> = {
        id: mapId,
    };
    const sitemap = sitemaps.find((s) => s.id === sitemapId);
    if (sitemap == null) {
        throw new Error("Tried to select a sitemap which does not exist.");
    }
    const sitemapVersions = sitemap.versions;
    if (sitemapVersions === undefined) {
        throw new Error("Selected sitemap has no versions.");
    }
    if (sitemapConfigs.length === 0 || !sitemapConfigs.some((sc) => sc.sitemapId === sitemapId)) {
        const latestVersion = sitemapVersions[sitemapVersions.length - 1];
        if (latestVersion.type === SitemapVersionType.TwoDimensional) {
            const updatedConfigs = [
                ...sitemapConfigs,
                {
                    sitemapId,
                    version2dId: latestVersion.id,
                    version2dVisible: visible,
                },
            ];
            updatedMap.sitemapConfigs = updatedConfigs;
        } else if (latestVersion.type === SitemapVersionType.ThreeDimensional) {
            const updatedConfigs = [
                ...sitemapConfigs,
                {
                    sitemapId,
                    version3dId: latestVersion.id,
                    version3dVisible: visible,
                },
            ];
            updatedMap.sitemapConfigs = updatedConfigs;
        }
    } else {
        const index = sitemapConfigs.findIndex((sc) => sc.sitemapId === sitemapId);
        const updatedConfigs = [...sitemapConfigs];
        const updatedConfig = { ...updatedConfigs[index] };
        if (versionType === SitemapVersionType.TwoDimensional || versionType == null) {
            updatedConfig.version2dVisible = visible;
        }
        if (versionType === SitemapVersionType.ThreeDimensional || versionType == null) {
            updatedConfig.version3dVisible = visible;
        }
        updatedConfigs[index] = updatedConfig;
        updatedMap.sitemapConfigs = updatedConfigs;
    }
    return updatedMap;
}

export function versionExists(sitemapId: string, versionId: string, sitemaps: Sitemap[]): boolean {
    const sitemap = sitemaps.find((s) => s.id === sitemapId);
    if (!sitemap || !sitemap.versions) return false;
    return sitemap.versions.some((v) => v.id === versionId);
}

// Returns the sitemap version id of the most recently created version for the given sitemap
// Throws an error if no versions are found for the sitemap
export function getLatestVersionId(sitemapId: string, sitemaps: Sitemap[], versionType: SitemapVersionType): string {
    const sitemap = sitemaps.find((s) => s.id === sitemapId);
    if (!sitemap || !sitemap.versions || sitemap.versions.length === 0) {
        throw new Error(`No versions found for sitemap with id: ${sitemapId}`);
    }
    if (versionType != null && !sitemap.versions.some((v) => v.type === versionType)) {
        throw new Error(`No versions of type ${versionType} found for sitemap with id: ${sitemapId}`);
    }
    const filteredVersions = sitemap.versions.filter((v) => v.type === versionType);
    return filteredVersions[filteredVersions.length - 1].id;
}

export function updateConfigWithValidVersion(config: MapSitemapConfig, sitemaps: Sitemap[]): MapSitemapConfig {
    const updatedConfig = { ...config };

    // Check for invalid 2D version
    if (!versionExists(config.sitemapId, config.version2dId, sitemaps)) {
        try {
            const latestVersion2dId = getLatestVersionId(config.sitemapId, sitemaps, SitemapVersionType.TwoDimensional);
            updatedConfig.version2dId = latestVersion2dId;
        } catch {
            updatedConfig.version2dId = null;
        }
    }

    // Check for invalid 3D version
    if (!versionExists(config.sitemapId, config.version3dId, sitemaps)) {
        try {
            const latestVersion3dId = getLatestVersionId(config.sitemapId, sitemaps, SitemapVersionType.ThreeDimensional);
            updatedConfig.version3dId = latestVersion3dId;
        } catch {
            updatedConfig.version3dId = null;
        }
    }

    // If all version ids are null, the config should be removed
    if (updatedConfig.version2dId == null && updatedConfig.version3dId == null) {
        return null;
    }

    return updatedConfig;
}

export function removeInvalidConfigurations(configs: MapSitemapConfig[], sitemaps: Sitemap[]): MapSitemapConfig[] {
    return configs.map((config) => updateConfigWithValidVersion(config, sitemaps)).filter((config) => config !== null);
}

export function hasSitemapVersionLevelsOfType(sitemap: Sitemap, versionType: SitemapVersionType): boolean {
    return sitemap.versions.some((version) => version.type === versionType && version.sitemapVersionLevels.length > 0);
}

export function allExistingSitemapTypesVisible(sitemap: Sitemap, sitemapConfigs: MapSitemapConfig[]): boolean {
    const selectedSitemapConfig = sitemapConfigs.find((s) => s.sitemapId === sitemap.id);
    if (!selectedSitemapConfig) return false;
    const has2DLevels = hasSitemapVersionLevelsOfType(sitemap, SitemapVersionType.TwoDimensional);
    const has3DLevels = hasSitemapVersionLevelsOfType(sitemap, SitemapVersionType.ThreeDimensional);

    let visible = true;
    if (has2DLevels) {
        visible = visible && selectedSitemapConfig.version2dVisible;
    }
    if (has3DLevels) {
        visible = visible && selectedSitemapConfig.version3dVisible;
    }
    return visible;
}

export function someSitemapTypesVisible(sitemap: Sitemap, sitemapConfigs: MapSitemapConfig[]): boolean {
    const selectedSitemapConfig = sitemapConfigs.find((s) => s.sitemapId === sitemap.id);
    if (!selectedSitemapConfig) return false;
    const has2DLevels = hasSitemapVersionLevelsOfType(sitemap, SitemapVersionType.TwoDimensional);
    const has3DLevels = hasSitemapVersionLevelsOfType(sitemap, SitemapVersionType.ThreeDimensional);

    let visible = false;
    if (has2DLevels) {
        visible = visible || selectedSitemapConfig.version2dVisible;
    }
    if (has3DLevels) {
        visible = visible || selectedSitemapConfig.version3dVisible;
    }
    return visible;
}
